import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { RatingIcons } from '@packages/modules/src/RatingIcons/RatingIcons';
import { Card } from '@packages/shared/src/components/Card/Card';
import { Stack } from '@packages/shared/src/components/Stack/Stack';
import { CardContent, CardHeader } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';
import { ReviewFragmentFragmentDoc } from '@packages/gql/generated/shopping/ReviewFragmentFragmentDoc';
import { Response } from '../Response';
import { ReviewMenu } from '../ReviewMenu';
import { ReviewTags } from '../ReviewTags';

/* GraphQL */ `
  fragment ReviewFragment on RatingReview {
    id
    created
    description
    rating
    reviewer {
      nickname
      location
    }
    reviewTags {
      ...ReviewTagsFragment
    }
    title
    responses {
      ...ResponseFragment
    }
  }
`;

const message = defineMessage({
  defaultMessage: 'von {authorNickName} | {created}',
  id: 'rating.author',
});

type ReviewProps = {
  /**
   * Review fragment data (__typename RatingReview)
   */
  reviewData: FragmentType<typeof ReviewFragmentFragmentDoc>;
  /**
   * Whether to show action menu
   * @default true
   */
  showMenu?: boolean;
};

export const Review = ({ reviewData, showMenu = true }: ReviewProps) => {
  const { id, reviewer, rating, created, title, description, responses, reviewTags } =
    getFragmentData(ReviewFragmentFragmentDoc, reviewData);
  const { formatMessage } = useIntl();

  return (
    <Card sx={{ width: '100%' }}>
      <CardHeader
        sx={{ py: 1, borderBottom: '1px solid', borderColor: 'grey.main' }}
        action={showMenu && <ReviewMenu id={id} />}
        title={
          <Stack spacing={0.5}>
            <RatingIcons avgRating={rating} size="small" reviewCount={0} />
            <p className="text-size-body2 text-color-text-darktransparent">
              {formatMessage(message, { authorNickName: reviewer.nickname, created })}
            </p>
          </Stack>
        }
        disableTypography
      />
      <CardContent>
        <Stack spacing={1}>
          <div>
            <p className="text-size-body2 font-bold">{title}</p>
            <p className="text-size-body2 whitespace-pre-wrap">{description}</p>
          </div>
          {reviewTags && <ReviewTags reviewTagsData={reviewTags} />}
        </Stack>
        {responses?.map((response, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Response responseData={response} sx={{ mt: 1.675 }} key={i} />
        ))}
      </CardContent>
    </Card>
  );
};
